import App from 'app/app.js';
import 'app/store/ContentStore.js';
import 'app/store/UserProfileStore.js';
import 'app/service/StorageService.js';
import 'app/service/AnalyticsService.js';
import 'app/service/StatsdService.js';
import 'app/service/IdentityService.js';
import 'app/service/SessionSyncService.js';
import '@app/service/HotjarSessionMappingService';

function InitService(
  ContentStore,
  StorageService,
  RouterService,
  AnalyticsService,
  StatsdService,
  IdentityService,
  UserProfileStore,
  $window,
  $qHelperService,
  SessionSyncService,
  $q,
  FeatureFlagService,
  HotjarSessionMappingService
) {
  // @param capturedLastAccessTime  mlt.last.access, read in index.gsp before the app bundle even
  //   loaded (see AppModule.ngDoBootstrap). AngularJS's own bootstrap — which runs before this
  //   function is called at all — already touches storage (e.g. ShoppingCartStore.repriceCart's
  //   error handler), so reading it here, even as the first line, would already be too late.
  this.init = function (capturedLastAccessTime) {
    const version = angular.element('#app-version').val();

    // MLC-13290: fetched here (see SessionSyncService.js for why the fetch happens at all) and
    // passed in, rather than called from StorageService, so resetDataIfNecessary stays the single
    // place that decides whether stored data is usable and StorageService needs no HTTP.
    //
    // Off skips the fetch entirely (no HTTP in the bootstrap gate) rather than discarding its
    // answer. undefined is resetDataIfNecessary's existing spelling for "not asked", which also
    // turns off the max-age check.
    const sessionSyncEnabled = FeatureFlagService.isEnabled('SESSION_SYNC');
    const serverStateReady = sessionSyncEnabled ? SessionSyncService.fetchServerSessionState() : $q.when(undefined);

    const storageReady = serverStateReady.then((serverSessionState) =>
      StorageService.resetDataIfNecessary(version, serverSessionState, capturedLastAccessTime)
    );

    // Fire-and-forget: not one of blockingPromises, since losing this costs an uncorrelated
    // Hotjar recording, not app functionality, and must never delay the splash screen. Reuse the
    // SESSION_SYNC response when it exists; otherwise Hotjar fetches its independent session state.
    if (FeatureFlagService.isEnabled('HOTJAR_SESSION_MAPPING')) {
      if (sessionSyncEnabled) {
        serverStateReady.then((serverSessionState) =>
          HotjarSessionMappingService.identifySession(serverSessionState?.instanceId)
        );
      } else {
        HotjarSessionMappingService.mapSession();
      }
    }

    // all methods that have to complete before init-ing the rest of the app (via RouterService.init)
    const blockingPromises = [
      $q.all([ContentStore.preloadContent(), storageReady]).then(([, dataWasReset]) => {
        if (!dataWasReset) StorageService.deserialize();
      })
    ];

    if ($window.ltSsoConfig.identityEnabled) {
      const identityPromise = $window.zuulPromise.then((result) =>
        IdentityService.initIdentity(result.token, result.error)
      );
      blockingPromises.push(identityPromise);
    } else {
      blockingPromises.push(UserProfileStore.loadLegacyUser());
    }

    $qHelperService.allSettled(blockingPromises).then(() => {
      angular.element('#splash').remove();
      RouterService.init();
      StatsdService.logPageLoadTime(new Date() - $window.pageStart);
    });

    window.addEventListener('beforeunload', () => {
      AnalyticsService.recordLeaveWebsite();
    });
  };
}

App.service('InitService', [
  'ContentStore',
  'StorageService',
  'RouterService',
  'AnalyticsService',
  'StatsdService',
  'IdentityService',
  'UserProfileStore',
  '$window',
  '$qHelperService',
  'SessionSyncService',
  '$q',
  'FeatureFlagService',
  'HotjarSessionMappingService',
  InitService
]);
